import React, { useState } from 'react';
import './SizeChart.css';

const SizeChart = ({ category, isOpen, onClose }) => {
  const [selectedChart, setSelectedChart] = useState('women');

  const sizeCharts = {
    women: {
      title: "Women's Size Chart",
      headers: ["Size", "Bust (inches)", "Waist (inches)", "Hips (inches)", "Length (inches)"],
      data: [
        ["XS", "32-34", "24-26", "34-36", "37-39"],
        ["S", "34-36", "26-28", "36-38", "38-40"],
        ["M", "36-38", "28-30", "38-40", "39-41"],
        ["L", "38-40", "30-32", "40-42", "40-42"],
        ["XL", "40-42", "32-34", "42-44", "41-43"],
        ["XXL", "42-44", "34-36", "44-46", "42-44"]
      ]
    },
    men: {
      title: "Men's Size Chart",
      headers: ["Size", "Chest (inches)", "Waist (inches)", "Shoulder (inches)", "Length (inches)"],
      data: [
        ["S", "36-38", "30-32", "17-18", "27-28"],
        ["M", "38-40", "32-34", "18-19", "28-29"],
        ["L", "40-42", "34-36", "19-20", "29-30"],
        ["XL", "42-44", "36-38", "20-21", "30-31"],
        ["XXL", "44-46", "38-40", "21-22", "31-32"],
        ["XXXL", "46-48", "40-42", "22-23", "32-33"]
      ]
    },
    kids: {
      title: "Kids Size Chart",
      headers: ["Size", "Age", "Height (inches)", "Chest (inches)", "Waist (inches)"],
      data: [
        ["2T", "2 Years", "32-35", "20-21", "19-20"],
        ["3T", "3 Years", "35-38", "21-22", "20-21"],
        ["4T", "4 Years", "38-41", "22-23", "21-22"],
        ["5T", "5 Years", "41-44", "23-24", "22-23"],
        ["6", "6 Years", "44-47", "24-25", "23-24"],
        ["8", "8 Years", "47-50", "25-26", "24-25"],
        ["10", "10 Years", "50-53", "26-27", "25-26"],
        ["12", "12 Years", "53-56", "27-28", "26-27"]
      ]
    },
    shoes: {
      title: "Shoe Size Chart",
      headers: ["US Size", "UK Size", "EU Size", "Foot Length (inches)", "Foot Length (cm)"],
      data: [
        ["5", "3", "36", "9.25", "23.5"],
        ["6", "4", "37", "9.5", "24.1"],
        ["7", "5", "38", "9.75", "24.8"],
        ["8", "6", "39", "10", "25.4"],
        ["9", "7", "40", "10.25", "26.0"],
        ["10", "8", "41", "10.5", "26.7"],
        ["11", "9", "42", "10.75", "27.3"],
        ["12", "10", "43", "11", "27.9"]
      ]
    }
  };

  const getCategoryChart = () => {
    if (category === 'man' || category === 'men') return 'men';
    if (category === 'kids' || category === 'children') return 'kids';
    if (category === 'accessories' || category === 'shoes') return 'shoes';
    return 'women'; // default
  };

  const currentChart = sizeCharts[selectedChart] || sizeCharts[getCategoryChart()];

  if (!isOpen) return null;

  return (
    <div className="size-chart-overlay" onClick={onClose}>
      <div className="size-chart-modal" onClick={(e) => e.stopPropagation()}>
        <div className="size-chart-header">
          <h2>Size Guide</h2>
          <button className="close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="size-chart-tabs">
          <button 
            className={`size-tab ${selectedChart === 'women' ? 'active' : ''}`}
            onClick={() => setSelectedChart('women')}
          >
            <i className="fas fa-female"></i>
            Women
          </button>
          <button 
            className={`size-tab ${selectedChart === 'men' ? 'active' : ''}`}
            onClick={() => setSelectedChart('men')}
          >
            <i className="fas fa-male"></i>
            Men
          </button>
          <button 
            className={`size-tab ${selectedChart === 'kids' ? 'active' : ''}`}
            onClick={() => setSelectedChart('kids')}
          >
            <i className="fas fa-child"></i>
            Kids
          </button>
          <button 
            className={`size-tab ${selectedChart === 'shoes' ? 'active' : ''}`}
            onClick={() => setSelectedChart('shoes')}
          >
            <i className="fas fa-shoe-prints"></i>
            Shoes
          </button>
        </div>

        <div className="size-chart-content">
          <h3>{currentChart.title}</h3>
          
          <div className="size-table-container">
            <table className="size-table">
              <thead>
                <tr>
                  {currentChart.headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentChart.data.map((row, index) => (
                  <tr key={index}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex} className={cellIndex === 0 ? 'size-cell' : ''}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="size-guide-tips">
            <h4><i className="fas fa-lightbulb"></i> How to Measure</h4>
            <div className="measurement-tips">
              {selectedChart === 'women' && (
                <ul>
                  <li><strong>Bust:</strong> Measure around the fullest part of your bust</li>
                  <li><strong>Waist:</strong> Measure around your natural waistline</li>
                  <li><strong>Hips:</strong> Measure around the fullest part of your hips</li>
                  <li><strong>Length:</strong> Measure from shoulder to desired length</li>
                </ul>
              )}
              {selectedChart === 'men' && (
                <ul>
                  <li><strong>Chest:</strong> Measure around the fullest part of your chest</li>
                  <li><strong>Waist:</strong> Measure around your natural waistline</li>
                  <li><strong>Shoulder:</strong> Measure from shoulder point to shoulder point</li>
                  <li><strong>Length:</strong> Measure from shoulder to desired length</li>
                </ul>
              )}
              {selectedChart === 'kids' && (
                <ul>
                  <li><strong>Height:</strong> Measure from head to toe without shoes</li>
                  <li><strong>Chest:</strong> Measure around the fullest part of chest</li>
                  <li><strong>Waist:</strong> Measure around natural waistline</li>
                  <li><strong>Age:</strong> Size recommendations based on average age</li>
                </ul>
              )}
              {selectedChart === 'shoes' && (
                <ul>
                  <li><strong>Foot Length:</strong> Measure from heel to longest toe</li>
                  <li><strong>Best Time:</strong> Measure feet in the evening when they're largest</li>
                  <li><strong>Both Feet:</strong> Measure both feet and use the larger measurement</li>
                  <li><strong>Standing:</strong> Measure while standing for accuracy</li>
                </ul>
              )}
            </div>
          </div>

          <div className="size-chart-note">
            <p><i className="fas fa-info-circle"></i> All measurements are approximate. For best fit, we recommend comparing with a similar garment you already own.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SizeChart; 